import axios from 'axios';
import { getCookie } from '../context/AuthContext';

// Base URL for the API
const BASE_URL = 'https://server.investain.com/crm/api';

// Global Axios instance with default headers
export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    withCredentials: true,
  },
});

// Add a request interceptor to include the token and x-api-key in headers
api.interceptors.request.use((config) => {
  const token = getCookie('access_token'); // Get token from cookies
 // console.log('Token retrieved in Axios interceptor:', token); // Debug log
  // Inject the access token by default, but honor an explicitly supplied
  // Authorization header (e.g. the logout endpoint requires the refresh token).
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add x-api-key header from environment variable
  if (process.env.REACT_APP_API_KEY) {
    config.headers['x-api-key'] = process.env.REACT_APP_API_KEY;
  }
  return config;
});

// Signup API
export const signup = async (userData) => {
  try {
    const response = await api.post('/user/signup', userData);

    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// Login API
export const login = async (credentials) => {
  try {
    const response = await api.post('/user/login', credentials);
    const { access_token, user_id, refresh_token, account_nature, kyc_completed, email } = response.data;
   
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};

// Verify Email API
export const resendverifyEmail = async (email) => {
  try {
    const response = await api.post('/user/resend_email', { email: email });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};
// Send OTP to phone number
export const sendPhoneVerificationCode = async (
  phoneNumber,
  recaptchaToken
) => {
  try {
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!recaptchaToken) {
      throw new Error('reCAPTCHA token is required');
    }

    const response = await api.post('/otp/request-otp', {
      platform: 'web',
      phoneNumber,
      recaptchaToken,
    });

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Failed to send verification code'
    );
  }
};

// Verify phone number with OTP
export const verifyPhoneNumber = async (phoneNumber, code, recaptchaToken) => {
  try {
    if (!recaptchaToken) {
      throw new Error('reCAPTCHA token is required');
    }

    const response = await api.post('/otp/verify-otp', {
      platform: 'web',
      phoneNumber,
      recaptchaToken,
      code,
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        'Invalid verification code'
    );
  }
};

// Logout API
// POST /api/user/logout - authenticated with the REFRESH token (not the access
// token). Invalidates the refresh token on the auth server, logs the user out
// of the trading server, and deactivates the local trading session.
export const logoutUser = async () => {
  const refresh_token = getCookie('refresh_token');
  if (!refresh_token) {
    // No refresh token to invalidate server-side.
    return null;
  }

  try {
    // Send the refresh token as the Bearer credential. The Authorization
    // header is set explicitly so the interceptor injects the refresh token
    // instead of the access token for this request.
    //
    // Send an empty JSON object {} as the body. Axios serializes a `null`
    // body into the JSON string "null", which strict JSON body parsers reject
    // with a 400 "not valid JSON" error. Passing `{}` sends a parseable empty
    // payload (the documented "empty request") and passes the sanity checks.
    const response = await api.post('/user/logout', {}, {
      headers: { Authorization: `Bearer ${refresh_token}` },
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error.message;
  }
};
